import { MultiTabQuestionnaire } from "@smartintegrity/questionnaire";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useCallback, useMemo } from "react";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { Col, Row } from "reactstrap";
import { createQuestionnaireAiSuggestionExtension } from "../../../components/QuestionnaireAiSuggestionExtension";
import { createQuestionnaireAssessmentGapExtension } from "../../../components/QuestionnaireAssessmentGapExtension";
import { createQuestionnaireGapExtension } from "../../../components/QuestionnaireGapExtension";
import { composeQuestionExtensions } from "../../../components/composeQuestionExtensions";
import { API_BASE_URL } from "src/modules/data-protection/constants";
import { buildComplianceObligationsMap } from "src/modules/data-protection/helpers/complianceObligations";
import { mapAnswersToSave } from "src/modules/data-protection/helpers/questionnaire";
import AssessmentPagesLayout from "../../../components/AssessmentPagesLayout";

const QuestionsLoadingPlaceholder = () => {
    return (
        <div>
            <Row className="mb-2">
                <Col lg="12">
                    <p style={{width: '100%', height: '55px'}} className={`dt-field dt-skeleton`}></p>
                </Col>
            </Row>
        </div>
    );
}

const RiskAssessmentQuestionnaire = ({
    questionsQuery,
    t,
    token,
    softwareId,
    setIsCreateAIOpen,
    aiRiskAssessmentId,
    questionnaireSlug,
    enableQuestionnaire,
    onValidSubmit,
    onProgressChange,
    complianceObligations = []
}) => {
    const updateQuestionnaireMutation = useMutation({
        mutationFn: async (payload) => {
            const response = await axios.post(
                `${API_BASE_URL}/report.update`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            )

            return response.data;
        },
        onSuccess({
            data
        }) {
            toast(t("Report Submitted"), {
                type: 'success'
            });

            onValidSubmit && onValidSubmit(data);
        },
        onError(error) {
            if (process.env.NODE_ENV === 'development') console.error(error)
            toast(t('Error!'), {
                type: "error"
            })
        }
    });

    const handleSubmitQuestionnaire = useCallback((payload) => {
        updateQuestionnaireMutation.mutate({
            questionAnswers: mapAnswersToSave(
                questionsQuery.data?.rawQuestions,
                payload
            ),
            software: softwareId,
            questionnaireType: questionnaireSlug,
            hardware: null,
            reportCase: aiRiskAssessmentId
        })
    }, [
        questionsQuery.data?.rawQuestions,
        updateQuestionnaireMutation,
        softwareId,
        questionnaireSlug,
        aiRiskAssessmentId
    ]);

    const obligationsByCode = useMemo(() => {
        return buildComplianceObligationsMap(complianceObligations);
    }, [complianceObligations]);

    const getPageIndicator = useCallback((page) => {
        const gapCount = (page?.data?.questions || []).reduce(
            (count, question) => {
                const code =
                    question?.systemData ?? question?.system_data;
                const obligations = code
                    ? obligationsByCode?.[code] || []
                    : [];

                return count + obligations.filter((obligation) => {
                    return (
                        obligation?.status == null ||
                        obligation.status === "gap"
                    );
                }).length;
            },
            0
        );

        return gapCount
            ? {
                label: `${gapCount} ${t("Documentation Gap")}`,
                variant: "gap"
            }
            : null;
    }, [obligationsByCode, t]);

    const settings = useMemo(() => {
        return {
            tabSettings: {
                showNumber: true,
                showProgressBar: true,
            },
            panelSettings: {
                showNumber: true,
                showProgressBar: false,
                prefix: ''
            },
            /**
             * The layout renders Back / Next / Save below the panel, outside
             * the library's form.
             */
            buttonsSettings: {
                submit: {
                    display: false
                },
                back: {
                    display: false
                }
            },
            fileUploaderOptions: {
                deleteRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId,
                        file
                    }) => {
                        return `${API_BASE_URL}/attachment/${file.id}/delete`
                    }
                },
                uploadRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId
                    }) => {
                        return `${API_BASE_URL}/report.uploadAttachment`
                    }
                }
            },
            questions: {
                showNumber: true
            },
            pagesLayoutContext: {
                navigationTitle: "EU AI Act Sections",
                getPageIndicator,
                onProgressChange,
                isSaving: updateQuestionnaireMutation.isLoading,
                canSave: enableQuestionnaire
            }
        }
    }, [
        token,
        updateQuestionnaireMutation.isLoading,
        enableQuestionnaire,
        getPageIndicator,
        onProgressChange
    ]);

    const questionnaireComponents = useMemo(() => {
        const QuestionExtension = composeQuestionExtensions(
            createQuestionnaireAiSuggestionExtension(),
            createQuestionnaireAssessmentGapExtension(),
            createQuestionnaireGapExtension(obligationsByCode)
        );

        return {
            PagesLayout: AssessmentPagesLayout,
            ...(QuestionExtension ? { QuestionExtension } : {})
        };
    }, [obligationsByCode]);

    if(questionsQuery.isFetching) {
        return (
            <QuestionsLoadingPlaceholder />
        )
    }

    return (
        <MultiTabQuestionnaire
            questionnaireUniqueId='ai-risk-assessment-questionnaire'
            className='gdpr'
            questions={questionsQuery.data?.mappedQuestions || []}
            defaultValues={questionsQuery.data?.mappedAnswers || []}
            settings={settings}
            components={questionnaireComponents}
            // onQuestionnaireCompleteStatusChanged={}
            onSubmit={handleSubmitQuestionnaire}
        />
    )
}

export default withNamespaces()(
    connect((state) => {
        const { token, user } = state.Login;

        return {
            token,
            user
        };
    })(
        RiskAssessmentQuestionnaire
    )
);